import * as React from 'react';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Divider,
  Card,
} from '@material-ui/core';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import 'hammerjs';
// Components
import WidgetTotalCountTile from '../WidgetTotalCountTile';
import { NodeVersionInfo } from '../../Interfaces/Versions/NodeVersionInfo';
import { getDaysDiff } from '../../utils/utils';
import { fetchNodeVersionInfo } from '../../store/Versions/VersionsActions';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  SeriesClickEvent,
  LegendItemClickEvent,
} from '@progress/kendo-react-charts';
import { saveVersionsGridState } from '../../store/UserSettings/UserSettingsActions';
import { MAX_DAYS_FOR_ONLINE_STATUS } from '../../utils/constants';
import OverviewWidgetPieChart from '../OverviewWidgetPieChart';
import { saveHealthFilters } from '../../store/UserSettings/UserSettingsActions';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { ASSET } from '../../utils/constants';
import { ApiEnums } from '../../Interfaces/ApiEnums';

// Styles
const useStylesHealthOverview = makeStyles((theme: Theme) =>
  createStyles({
    widgetBox: {
      margin: theme.spacing(2),
      width: '450px',
    },
    flexBox: {
      flex: 1,
      flexDirection: 'row',
      display: 'flex',
    },
    widgetItem: {
      flex: 1,
      border: '1px solid #ccc',
    },
    tileContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    totalCountContainer: {
      display: 'flex',
      height: 140,
      width: '200px',
    },
    overViewTitle: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export interface SoftwareVersion {
  name: string;
  count: number;
}

export default function HealthOverview() {
  const classes = useStylesHealthOverview();
  const dispatch: Dispatch<any> = useDispatch();

  const nodeVersionInfo: NodeVersionInfo[] = useSelector(
    (state: ApplicationState) => state.versionsReducer.nodeVersionInfo
  );

  const isFetchingVersionInfo: boolean = useSelector(
    (state: ApplicationState) => state.versionsReducer.isFetchingVersionInfo
  );

  const [lanesOfflineCount, setLanesOfflineCount] = React.useState<number>(0);

  useEffect(() => {
    fetchNodeVersionInfo(dispatch, false);
  }, []);

  useEffect(() => {
    getTotalLanesOffline();
  }, [nodeVersionInfo]);

  const getLanesOfflineTileColor = () => {
    if (lanesOfflineCount <= 0) return 'green';
    else if (lanesOfflineCount <= 50) return '#FFB600';
    else return 'red';
  };

  const getTotalLanesOffline = () => {
    var count: number = 0;
    for (let i = 0; i < nodeVersionInfo.length; ++i) {
      var daysDiff: number = getDaysDiff(
        nodeVersionInfo[i].LastHeartbeat.toString()
      );

      if (daysDiff > MAX_DAYS_FOR_ONLINE_STATUS) {
        ++count;
      }
    }
    setLanesOfflineCount(count);
  };

  const [softwareVersion, setSoftwareVersion] = React.useState<
    SoftwareVersion[]
  >([]);

  const [listSoftwareVersion, setListSoftwareVersion] = React.useState<
    SoftwareVersion[]
  >([]);

  const getSoftwareVersion = (deliveryCode: string, buildNumber: string) => {
    return deliveryCode + ' ' + buildNumber;
  };

  useEffect((): ReturnType<React.EffectCallback> => {
    let list: SoftwareVersion[] = [];
    const softwareVersion = nodeVersionInfo
      .map((item: NodeVersionInfo) =>
        getSoftwareVersion(item.DeliveryCode, item.BuildNumber)
      )
      .filter((value, index, self) => self.indexOf(value) === index);

    for (let i = 0; i < softwareVersion.length; ++i) {
      var data: SoftwareVersion = {
        name: softwareVersion[i],
        count: 0,
      };

      if (data.name !== 'N/A N/A' || undefined) list.push(data);
    }
    setSoftwareVersion(list);
    return (): void => {};
  }, [nodeVersionInfo]);

  useEffect((): ReturnType<React.EffectCallback> => {
    let list: SoftwareVersion[] = [...softwareVersion];
    for (let i = 0; i < nodeVersionInfo.length; i++) {
      let index: any = softwareVersion.findIndex(
        (x: SoftwareVersion) =>
          x.name ===
          getSoftwareVersion(
            nodeVersionInfo[i].DeliveryCode,
            nodeVersionInfo[i].BuildNumber
          )
      );
      if (index !== -1) list[index].count++;
    }
    setListSoftwareVersion(list);
  }, [softwareVersion]);

  const labelContent = (props: any) => props.dataItem.count;

  const onSoftwareVersionSeriesClick = (event: SeriesClickEvent) => {
    let dataItemName = event.dataItem.name;

    let newDataItem: string = dataItemName.split(' ');

    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'BuildNumber',
            operator: 'eq',
            value: newDataItem[1],
          },
          { field: 'DeliveryCode', operator: 'eq', value: newDataItem[0] },
        ],
      },
    };
    saveVersionsGridState(dispatch, gridState);
  };

  const onSoftwareVersionLegendClick = (event: LegendItemClickEvent) => {
    let series: any = event.series;

    let pointIndex = series.data[event.pointIndex].name;

    let newSeriesData: any = pointIndex.split(' ');

    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'BuildNumber',
            operator: 'eq',
            value: newSeriesData[1],
          },
          { field: 'DeliveryCode', operator: 'eq', value: newSeriesData[0] },
        ],
      },
    };
    saveVersionsGridState(dispatch, gridState);
  };

  const widgetLanesNotReportingCountTileClick = () => {
    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.LANE_STATUS,
          operator: 'eq',
          value: {
            Status: ApiEnums.NodeReportingStatus.NotReporting,
            ActiveThreshold: MAX_DAYS_FOR_ONLINE_STATUS,
          },
        },
      ],
    };
    saveHealthFilters(dispatch, filter);

    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [],
      },
    };
    saveVersionsGridState(dispatch, gridState);
  };

  return (
    <React.Fragment>
      <Box className={classes.widgetBox}>
        <div className={classes.overViewTitle}>
          <StringResourceManager
            stringKey="HEALTH_SUMMARY"
            style="overViewTitle"
          />
        </div>
        <div className={classes.tileContainer}>
          <div className={classes.totalCountContainer}>
            <WidgetTotalCountTile
              title={GetString('LANES_OFFLINE')}
              count={lanesOfflineCount}
              bgColor={getLanesOfflineTileColor()}
              textColor="#fff"
              goToScreen="health"
              onClick={widgetLanesNotReportingCountTileClick}
              testId="overview_health_lanesOfflineCount"
            />
          </div>
        </div>
        <Divider />
        <div className={classes.flexBox}>
          <div className={classes.widgetItem}>
            <OverviewWidgetPieChart
              chartTitleText={GetString('SOFTWARE_VERSION')}
              seriesData={listSoftwareVersion}
              chartSeriesField={'count'}
              chartSeriesCategoryField={'name'}
              chartSeriesClick={onSoftwareVersionSeriesClick}
              legendItemClick={onSoftwareVersionLegendClick}
              labelContent={labelContent}
              goToScreen="health"
              isRefreshing={isFetchingVersionInfo}
            />
          </div>
        </div>
      </Box>
    </React.Fragment>
  );
}
